import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Customers, IBackendResponse, IUser } from '@zero/shared/util-models';
import { AuthState, AuthStateState } from '@zero/shared/util-services';
import { commonRoutes } from 'constants/web-app-constants';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, Subject, Subscription, from, merge, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { HandleError, HttpErrorHandler } from '../http/http-error-handler.service';
import { AuthInterceptor } from '../interceptors/auth.interceptor';

export interface IAuthentication {
  user: IUser;
  authState: AuthState;
}

export interface IAuthEvent {
  type: string;
}

@Injectable()
export abstract class AuthService implements OnDestroy {
  public redirectUrl: string;
  public resolveInitPromise: Function;
  public clearPushNotifications$: Subject<boolean>;

  /**
   * private, damit alle über getAuth() gehen müssen. Damit wird gewährleistet dass das token automatisch von
   * Amplify refreshed wird (durch Auth.getSession()).
   */
  protected currentAuth: IAuthentication;
  protected handleError: HandleError;
  protected loginEvents$: Subject<IAuthEvent> = new Subject<IAuthEvent>();
  protected toUnsubcribe: Subscription[] = [];
  protected loginEventsMapped: Observable<IAuthentication>;
  protected amplifyEventsMapped$: Observable<IAuthentication>;
  protected authChanged: Observable<IAuthentication>;
  protected initPromise: Promise<boolean>;
  protected allAuthStateChanges$: Observable<IAuthentication>;
  protected loggedInObservable: Observable<boolean>;
  protected isAdmin$: Observable<boolean>;
  protected isCustomerObservable$: Observable<boolean>;
  protected isPotentialCustomer$: Observable<boolean>;
  protected remainingTimeToForcedLogout$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public static isState(state: IAuthentication, stateToCompare: string): boolean {
    return state?.authState?.state === stateToCompare;
  }

  protected constructor(
    protected http: HttpClient,
    protected httpErrorHandler: HttpErrorHandler,
    protected router: Router,
  ) {
    this.initPromise = new Promise((resolve) => {
      this.resolveInitPromise = resolve;
    });

    this.amplifyEventsMapped$ = this.createAmplifyEventsMappedObservable();
    this.loginEventsMapped = this.loginEvents$.pipe(
      map((event: IAuthEvent) => {
        const returnedAuth: IAuthentication = {
          authState: { state: event.type as AuthStateState, user: undefined },
          user: undefined,
        };

        return returnedAuth;
      }),
    );
    this.authChanged = merge(this.amplifyEventsMapped$, this.loginEventsMapped);
    this.toUnsubcribe.push(
      this.authChanged.subscribe((value: IAuthentication) => {
        this.setNewAuth(value);
      }),
    );

    this.allAuthStateChanges$ = merge(from(this.initPromise), this.authChanged).pipe(map(() => this.currentAuth));
    this.createStateObservables();
    this.handleError = httpErrorHandler.createHandleError('Authservice');
    inject(HTTP_INTERCEPTORS).forEach((interceptor) => {
      if (interceptor['setAuthService']) {
        (interceptor as AuthInterceptor).setAuthService(this);
      }
    });
  }

  public abstract createAmplifyEventsMappedObservable(): Observable<IAuthentication>;

  ngOnDestroy(): void {
    this.toUnsubcribe.forEach((elem) => {
      elem.unsubscribe();
    });
  }

  public isLoggedIn$(): Observable<boolean> {
    return this.loggedInObservable;
  }

  public isNewPasswordRequired(): boolean {
    return this.currentAuth?.authState?.state === 'requireNewPassword';
  }

  public isPasswordResetRequired(): boolean {
    return this.currentAuth?.authState?.state === 'passwordResetRequired';
  }

  public isMFARequired(): boolean {
    return this.currentAuth?.authState?.state === 'confirmSignIn';
  }

  public isPasswordForgotten(): boolean {
    return this.currentAuth?.authState?.state === 'forgotPassword';
  }

  public explicitLogout(): void {
    this.logout(
      false,
      false,
      undefined,
      {
        reason: 'explicit logout through customer',
      },
      () => window.location.replace(environment.logoutUrl),
    );
  }

  public isUserAdmin$(): Observable<boolean> {
    return this.isAdmin$;
  }

  public isCustomer$(): Observable<boolean> {
    return this.isCustomerObservable$;
  }

  public isPotentialCustomer(): Observable<boolean> {
    return this.isPotentialCustomer$;
  }

  public setNewAuth(newAuth: any): void {
    if (typeof this.currentAuth === 'undefined') {
      this.resolveInitPromise('resolved');
    }
    this.currentAuth = newAuth;
  }

  public abstract login(userName: string, password: string): Observable<IBackendResponse<any>>;

  public abstract logout(global?: boolean, reload?: boolean, messageAfterLogout?: string, serverLogInfo?: any, cb?: () => void): void;

  public abstract logout$(serverLogInfo?: any): Observable<boolean>;

  public abstract getAuthToken(): Observable<string>;

  public abstract getAccessToken(): Observable<string>;

  public abstract getCurrentUserName(): string;

  public abstract getCurrentVerifiedPhoneNumber(): string | undefined;

  public abstract getCurrentCustomerData(): Customers;

  public abstract refreshSession(): Observable<void>;

  public abstract setNewPassword(inputNewPassword: string): Observable<IBackendResponse<any>>;

  public abstract setPasswordAfterForgotten(
    username: string,
    verificationCode: string,
    password: string,
  ): Observable<IBackendResponse<any>>;

  public abstract forgotPassword(username: string): Observable<boolean>;

  public setPasswordForgottenState() {
    this.loginEvents$.next({ type: 'forgotPassword' });
  }

  public abstract sendMFACode(mfaCode: string): Observable<IBackendResponse<any>>;

  public sendConfirmationDataForEmailConfirmation(email: string, code: string, adAgreed: boolean): Observable<IBackendResponse<boolean>> {
    return this.http
      .post<any>(`${environment.apihost}/api/user/registrationconfirmation`, {
        email: email,
        code: code,
        adAgreed: String(adAgreed),
      })
      .pipe(map((result) => result.successful));
  }

  public redirectAfterLogin(): void {
    if (this.redirectUrl && this.redirectUrl !== commonRoutes.prelogin.home) {
      this.router.navigateByUrl(this.redirectUrl);
    } else {
      this.isUserInGroup('UserAdmin')
        .pipe(
          switchMap((isAdmin: boolean) => {
            if (isAdmin) {
              this.router.navigate(['/useradmin']);
              return of('dummy');
            } else {
              return this.isUserInGroup('Kunden').pipe(
                switchMap((isCustomer: boolean) => {
                  if (isCustomer) {
                    this.router.navigate([commonRoutes.postlogin.member_home], { state: { redirectAfterLogin: true } });
                    return of('dummy');
                  } else {
                    return this.isUserInGroup('Interessenten').pipe(
                      switchMap((isPotentialCustomer: boolean) => {
                        if (isPotentialCustomer) {
                          this.router.navigate([commonRoutes.postlogin.onboarding.redirect]);
                        }
                        return of('dummy');
                      }),
                    );
                  }
                }),
              );
            }
          }),
        )
        .subscribe(null, null, () => {
          // do nothing
        }); // we don't need the value
    }
  }

  protected clearCurrentAuth(): void {
    this.currentAuth = null;
  }

  protected createStateObservables() {
    this.loggedInObservable = this.allAuthStateChanges$.pipe(
      map((state: IAuthentication) => AuthService.isState(state, 'signedIn')),
      distinctUntilChanged(),
    );

    this.isAdmin$ = this.loggedInObservable.pipe(
      switchMap((loggedIn) => {
        if (loggedIn === true) {
          return this.isUserInGroup('UserAdmin');
        } else {
          return of(false);
        }
      }),
    );

    this.isCustomerObservable$ = this.loggedInObservable.pipe(
      switchMap((loggedIn) => {
        if (loggedIn === true) {
          return this.isUserInGroup('Kunden');
        } else {
          return of(false);
        }
      }),
    );

    this.isPotentialCustomer$ = this.loggedInObservable.pipe(
      switchMap((loggedIn) => {
        if (loggedIn === true) {
          return this.isUserInGroup('Interessenten');
        } else {
          return of(false);
        }
      }),
    );
  }

  protected abstract getUser(): Observable<any>;

  protected abstract getUserSession(): Observable<any>;

  private isUserInGroup(groupName: string): Observable<boolean> {
    if (!this.currentAuth?.authState?.user) {
      return of(false);
    }

    if (!this.currentAuth.authState.user.signInUserSession) {
      return this.getUserSession().pipe(
        map((session) => {
          if (!session) {
            if (this.currentAuth?.authState?.state === 'signedIn') {
              this.loginEvents$.next({ type: 'signedOut' });
            }
            return false;
          }
          if (session.idToken) {
            return session.idToken.payload['cognito:groups'].indexOf(groupName) > -1;
          } else {
            return false;
          }
        }),
      );
    } else {
      return of(this.currentAuth.authState.user.signInUserSession.idToken.payload['cognito:groups'].indexOf(groupName) > -1);
    }
  }

  public getRemainingTimeToForcedLogout$(): Observable<number> {
    return this.remainingTimeToForcedLogout$.asObservable();
  }

  public abstract verifyMobileNrForCurrentUser(verificationCode: string): Promise<boolean>;

  public abstract initiateMobileNrVerificationForCurrentUser(): Promise<void>;

  public setWidgetLoggedInCookie(): void {
    const name = 'zero-logged-in';
    const expiration = 7; // days

    const date = new Date();
    date.setTime(date.getTime() + expiration * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${true}; expires=${date.toUTCString()}; path=/`;
  }

  public abstract logoutMaintenance(): void;
}
